import cx from 'classnames';
import moment from 'moment';
import React from 'react';

import {
  ButtonBase, FormControlLabel, Switch, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import CampaignAPNsReduceModal from 'components/CampaignInfoCards/CampaignAPNsReduceModal';
import CampaignCostsTooltip from 'components/CampaignInfoCards/CampaignCostsTooltip';

import { campaignEventTypeCanBeBilledAnnually, numberWithCommas } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  infoCard: {
    margin: '20px auto 0',
    padding: '10px 24px',
    borderRadius: '8px',
    position: 'relative',
    maxWidth: '315px',
    height: '70px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontSize: '14px',
    lineHeight: '18px',
    color: theme.palette.black.main,

    '& > h4': {
      font: 'inherit',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    '& > p': {
      font: 'inherit',
      fontWeight: 'normal',
    },
  },
  numberOfApnsInfoCard: {
    '& svg': {
      marginLeft: '10px',
      verticalAlign: 'middle',
    },
    '& > button': {
      cursor: 'pointer',
      position: 'absolute',
      bottom: '10px',
      right: '24px',
      textDecoration: 'underline',
      fontFamily: 'Overpass Mono',
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '16px',
      color: theme.palette.orange.main,
    },
  },
  pricingInfoCard: {
    '& > p': {
      fontSize: '14px',
      lineHeight: '18px',
    },
    '& > span': {
      cursor: 'grab',
      position: 'absolute',
      bottom: '10px',
      right: '24px',
      textDecoration: 'underline',
      fontFamily: 'Overpass Mono',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      color: theme.palette.orange.main,
    },
  },
  annualBillingSwitch: {
    position: 'absolute',
    bottom: '20px',
    left: '24px',
    '& > label > span': {
      color: theme.palette.darkGray.main,
      fontSize: '13px',
      fontWeight: 500,
    },
  },
}));

const CampaignStartDateCard = ({ startDate }) => {
  const classes = useStyles();

  return (
    <div className={classes.infoCard} style={{ backgroundColor: '#e5fde2' }}>
      <h4>Campaign Start Date</h4>
      <p>{startDate ? moment(startDate).format('MMMM Do, YYYY') : 'TBD'}</p>
    </div>
  );
};

const CampaignEndDateCard = ({ endDate }) => {
  const classes = useStyles();

  return (
    <div className={classes.infoCard} style={{ backgroundColor: '#D5FCCF' }}>
      <h4>Campaign End Date</h4>
      <p>{endDate ? moment(endDate).format('MMMM Do, YYYY') : 'TBD'}</p>
    </div>
  );
};

const NumberOfApnsCard = ({
  apnsTotalSelectedCount,
  apnsTotalInAreaCount,
  valuesAreEstimates,
  apnsFarmingStoppedCount,
  isCreating,
  apnAvailableCounts = {},
  onReduceClick,
}) => {
  const classes = useStyles();

  const hasAvailableCounts = Object.keys(apnAvailableCounts).length !== 0;

  let cardTitleSuffix = '';
  if (valuesAreEstimates) cardTitleSuffix = ' (est.)';

  return (
    <div className={cx(classes.infoCard, classes.numberOfApnsInfoCard)} style={{ backgroundColor: '#D6F5FF' }}>
      <h4>{`Number of APNs${cardTitleSuffix}`}</h4>
      <p>
        {apnsTotalInAreaCount !== undefined && (
          apnsTotalSelectedCount < apnsTotalInAreaCount ? (
            <span>
              {`${numberWithCommas(apnsTotalSelectedCount)} Selected`}
              <Tooltip
                placement="top"
                disableFocusListener
                disableTouchListener
                title={`There are a total of ${numberWithCommas(apnsTotalInAreaCount)} APNs in the selected Farm Areas, some of which may be farmed by other users and be unavailable to you, or excluded from your campaign due to your APN limit`}
              >
                <InfoOutlinedIcon />
              </Tooltip>
            </span>
          ) : `${numberWithCommas(apnsTotalSelectedCount)} Selected`
        )}

        {!valuesAreEstimates
          ? apnsFarmingStoppedCount > 0 ? (
            <span>
              {`${numberWithCommas(apnsTotalSelectedCount)} Total, ${numberWithCommas(apnsTotalSelectedCount - apnsFarmingStoppedCount)} Active`}
              <Tooltip
                placement="top"
                disableFocusListener
                disableTouchListener
                title={`The total number of APNs less the ${apnsFarmingStoppedCount} you've disabled farming on`}
              >
                <InfoOutlinedIcon />
              </Tooltip>
            </span>
          ) : (<>{numberWithCommas(apnsTotalSelectedCount)}</>)
          : (
            apnsTotalInAreaCount === undefined ? (
              <span>
                {numberWithCommas(apnsTotalSelectedCount)}
              </span>
            ) : null
          )}
      </p>

      {isCreating && onReduceClick && apnsTotalSelectedCount > 0 && hasAvailableCounts && (
        <ButtonBase onClick={onReduceClick}>Reduce?</ButtonBase>
      )}
    </div>
  );
};

export const CampaignCostCard = ({
  valuesAreEstimates,
  campaignCosts,
  apnsTotalSelectedCount,
  isCreating,
  eventType,
  setIsAnnualBilling,
}) => {
  const classes = useStyles();

  let cardTitleSuffix = '';
  if (valuesAreEstimates) cardTitleSuffix = ' (est.)';

  // If they're on the create campaign screen, they can choose between annual/monthly billing.
  // Show a different screen for this case by checking the setIsAnnualBilling callback.
  if (isCreating) {
    return (
      <div
        className={cx(classes.infoCard, classes.pricingInfoCard)}
        style={{
          backgroundColor: '#B3EDFF',
          height: '125px',
          marginTop: '20px',
          justifyContent: 'flex-start',
          paddingTop: '18px',
          gap: 2,
        }}
      >
        <h4>{`Campaign Cost${cardTitleSuffix}`}</h4>
        {campaignCosts && apnsTotalSelectedCount > 0 ? (
          <>
            <p>
              {campaignCosts.isAnnualBilling
                ? `$${numberWithCommas((campaignCosts.annualEffectiveMonthly / 100).toFixed(2))}/mo - Save $${numberWithCommas((campaignCosts.annualDiscount / 100).toFixed(2))}!`
                : `$${numberWithCommas((campaignCosts.totalMonthlyCost / 100).toFixed(2))}/mo`}
            </p>

            {/* {campaignCosts.isAnnualBilling && (
              <h5 style={{ padding: '4px 0', fontWeight: 400, fontSize: '14px' }}>
                {`Save $${numberWithCommas((campaignCosts.annualDiscount / 100).toFixed(2))}! - Effectively $${numberWithCommas((campaignCosts.annualEffectiveMonthly / 100).toFixed(2))}/mo`}
              </h5>
            )} */}

            {/* Only allow annual billing toggle for campaigns that support annual billing (ABS/FF) */}
            {isCreating && campaignEventTypeCanBeBilledAnnually(eventType) && (
              <div className={classes.annualBillingSwitch}>
                <FormControlLabel
                  control={(
                    <Switch
                      color="primary"
                      checked={campaignCosts.isAnnualBilling}
                      onChange={(e) => setIsAnnualBilling(e.target.checked)}
                    />
                    )}
                  label={campaignCosts.isAnnualBilling ? 'Annual billing - 1 month free' : 'Monthly billing'}
                />
              </div>
            )}

            <span>
              <CampaignCostsTooltip
                text="Breakdown"
                campaignCosts={campaignCosts}
              />
            </span>
          </>
        ) : <p>N/A</p>}
      </div>
    );
  }

  return (
    <div className={cx(classes.infoCard, classes.pricingInfoCard)} style={{ backgroundColor: '#B3EDFF' }}>
      <h4>{`Campaign Cost${cardTitleSuffix}`}</h4>
      {campaignCosts && (
        <>
          <p>
            {campaignCosts.isAnnualBilling
              // ? `$${numberWithCommas((campaignCosts.annualTotal / 100).toFixed(2))} once`
              ? `$${numberWithCommas((campaignCosts.annualEffectiveMonthly / 100).toFixed(2))}/mo`
              : `$${numberWithCommas((campaignCosts.totalMonthlyCost / 100).toFixed(2))}/mo`}
          </p>

          <span>
            <CampaignCostsTooltip
              text="Breakdown"
              campaignCosts={campaignCosts}
            />
          </span>
        </>
      )}
    </div>
  );
};

function CampaignInfoCards({
  startDate, endDate, eventType, campaignCosts, valuesAreEstimates, apnsTotalSelectedCount, apnsFarmingStoppedCount, isCreating,

  // Optional, used for create campaign page
  apnAvailableCounts, apnSelectedCounts, setApnSelectedCounts, setApnsTotalSelectedCount, apnsTotalInAreaCount, setIsAnnualBilling,
}) {
  const { isAdmin, isAccountManager } = useAuthDataContext();

  const [reduceAPNsModalOpen, setReduceAPNsModalOpen] = React.useState(false);

  return (
    <GridContainer justifyContent="flex-start" alignContent="center">
      {apnAvailableCounts && Object.keys(apnAvailableCounts).length !== 0 && reduceAPNsModalOpen && (
        <CampaignAPNsReduceModal
          onClose={() => setReduceAPNsModalOpen(false)}
          apnAvailableCounts={apnAvailableCounts}
          apnSelectedCounts={apnSelectedCounts}
          setApnSelectedCounts={setApnSelectedCounts}
          setApnsTotalSelectedCount={setApnsTotalSelectedCount}
        />
      )}

      <GridItem xs={12} sm={6} xl={3}>
        <CampaignStartDateCard startDate={startDate} />
      </GridItem>
      <GridItem xs={12} sm={6} xl={3}>
        <CampaignEndDateCard endDate={endDate} />
      </GridItem>
      <GridItem xs={12} sm={6} xl={3}>
        <NumberOfApnsCard
          apnsTotalSelectedCount={apnsTotalSelectedCount}
          apnsTotalInAreaCount={apnsTotalInAreaCount}
          valuesAreEstimates={valuesAreEstimates}
          apnsFarmingStoppedCount={apnsFarmingStoppedCount}
          isCreating={isCreating}
          apnAvailableCounts={apnAvailableCounts}
          onReduceClick={() => setReduceAPNsModalOpen(true)}
        />
      </GridItem>

      {/* Only show the campaign cost card with the campaign info cards for campaigns being created or for admins.
          Otherwise we stuff it under the campaign settings for started campaigns. */}
      {(isCreating || isAdmin || isAccountManager) && (
        <GridItem xs={12} sm={6} xl={3}>
          <CampaignCostCard
            valuesAreEstimates={valuesAreEstimates}
            campaignCosts={campaignCosts}
            apnsTotalSelectedCount={apnsTotalSelectedCount}
            isCreating={isCreating}
            eventType={eventType}
            setIsAnnualBilling={setIsAnnualBilling}
          />
        </GridItem>
      )}
    </GridContainer>
  );
}

export default CampaignInfoCards;
